/* Keyboard shortcuts for the map's orders, and for ending the round.

One key per action. The cell orders are shown on the action bar's buttons and pressed while a cell
is selected; `endTurn` is the exception and belongs to the top bar, where no selection is needed.
The defaults are the first letter where English allows it; the player rebinds them in the profile
pop-up and the binding is kept per browser in localStorage (like the language — a device setting,
not a game one). A tiny pub/sub lets the bar re-render when the settings change.

All bindings share one namespace on purpose: `rebind` swaps rather than duplicates, and it can only
promise that if every key it might collide with is in `SHORTCUT_ACTIONS`. */

export const SHORTCUT_ACTIONS = [
  "march",
  "colonize",
  "settle",
  "strike",
  "city",
  "battle",
  "share",
  "tunnelLoad",
  "tunnelUnload",
  "discharge",
  "endTurn",
  "resetRoute",
]

export const DEFAULT_SHORTCUTS = {
  march: "m",
  // Colonize (owner, 2026-09-07): the founding party sent from your own city in one press. "O"
  // because C is the city view (S was the spy, removed 2026-10-26).
  colonize: "o",
  settle: "f",
  strike: "x",
  city: "c",
  // The battle view (owner, 2026-09-07): on the bar for a contested cell, and inside the city
  // view for the fight at its gates — the one place a key has to reach into a modal.
  battle: "b",
  // The pin — sharing the selected cell to the team chat. Not an order the server hears, but it
  // lives on the same bar under the same "a cell is selected" rules, so it binds like one.
  share: "p",
  // The tunnel's two orders (owner, 2026-09-29): ENTRAR / SALIR on a tunnel city of yours. "I" for
  // in and "U" for up and out — E is ending the round and X the silo's strike.
  tunnelLoad: "i",
  tunnelUnload: "u",
  // DAR DE BAJA (owner, 2026-09-30): humanity's units into workers on your own city. "D" for
  // discharge.
  discharge: "d",
  // Ending the round (owner, 2026-09-08). The one binding here that is not a cell order: it is
  // fired by `RoundHandler` in the top bar and needs no selection, so it is deliberately not on
  // the action bar's listener. It lives in this list anyway because `rebind` only guarantees no
  // two keys collide among the actions it can see — an end-turn key kept in a list of its own
  // would be free to shadow March.
  endTurn: "e",
  // Resetting the route being drawn (owner, 2026-10-26): the route planner's "Reset route", which
  // listens for it only while a route is being drawn. "R" for reset.
  resetRoute: "r",
}

/* What the settings pop-up calls each binding. A cell order is named as the action bar names it;
   `endTurn` is named by the button it presses, and the tunnel's two by what they do. */
export const SHORTCUT_LABEL_KEYS = {
  endTurn: "game.topBar.endTurnShort",
  // "Enter" / "Exit" alone, beside a keycap, read as the names of keys.
  tunnelLoad: "game.tunnel.enterShortcut",
  tunnelUnload: "game.tunnel.exitShortcut",
  resetRoute: "game.route.reset",
}

export const labelKeyFor = (action) => SHORTCUT_LABEL_KEYS[action] || `game.mapAction.${action}`

/* What a keycap reads. A bound space is a real choice for a key pressed every round, and printing
   it verbatim draws an empty cap. */
export const keycapLabel = (key) => (key === " " ? "␣" : key ? key.toUpperCase() : null)

const KEY = "hvz-shortcuts"
const listeners = new Set()

const normalise = (key) => (typeof key === "string" && key.length === 1 ? key.toLowerCase() : null)

export const loadShortcuts = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) || "{}")
    const merged = { ...DEFAULT_SHORTCUTS }
    for (const action of SHORTCUT_ACTIONS) {
      const key = normalise(stored[action])
      // `null` is a deliberate "no key"; anything else unknown falls back to the default.
      if (stored[action] === null) merged[action] = null
      else if (key) merged[action] = key
    }
    return merged
  } catch {
    return { ...DEFAULT_SHORTCUTS }
  }
}

export const saveShortcuts = (shortcuts) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(shortcuts))
  } catch {
    // Storage unavailable: the in-memory value still applies for this session.
  }
  listeners.forEach((listener) => listener(shortcuts))
}

export const subscribeShortcuts = (listener) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/* Bind `key` to `action`. If another action held that key, it takes over `action`'s old key — a
   swap — so no two orders ever share a key and rebinding never silently disables something. */
export const rebind = (shortcuts, action, key) => {
  const next = { ...shortcuts }
  const wanted = normalise(key)
  if (!wanted) {
    next[action] = null
    return next
  }
  const holder = SHORTCUT_ACTIONS.find((other) => other !== action && next[other] === wanted)
  if (holder) next[holder] = next[action]
  next[action] = wanted
  return next
}

export const actionForKey = (shortcuts, key) => {
  const pressed = normalise(key)
  if (!pressed) return null
  return SHORTCUT_ACTIONS.find((action) => shortcuts[action] === pressed) || null
}

/* Whether a keydown is meant for the page rather than a field or a modifier chord. */
export const isPlainKeyPress = (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey) return false
  const target = event.target
  const tag = target?.tagName
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return false
  return true
}
